import { ChangeEvent, useCallback, useState } from "react"
import { FavoritedProperty, Property, PropertyImage } from "../../types/property"
import { useNotification } from "@smoothincorg/smooth-ui"
import axios from "axios"
import { SMOOTH_LINE_API_URL } from "src/lib/constants"

type FormState = {
  url: string
  userId: string
  favorited_property?: Pick<FavoritedProperty, "is_brought"> & {
    atbb_status: string
    reins_status: string
  }
  property?: Pick<
    Property,
    | "id"
    | "building_name"
    | "rent_fee"
    | "management_fee"
    | "stations"
    | "specials"
    | "gratuity_fee"
    | "deposit"
    | "brokerage_fee"
    | "insurance_company"
    | "breadth_floor"
    | "madori"
    | "address"
    | "walks"
    | "building_age"
    | "build_const"
    | "build_type"
    | "other_initial_cost"
    | "introduction_status"
  >
  property_images?: PropertyImage[]
}

export type PropertyNewForm = ReturnType<typeof usePropertyNewForm>

export const usePropertyNewForm = () => {
  const [formState, setFormState] = useState<FormState>({
    url: "",
    userId: "",
    favorited_property: {
      is_brought: true,
      atbb_status: "unknown",
      reins_status: "unknown",
    },
    property: {
      id: undefined,
      building_name: undefined,
      rent_fee: undefined,
      management_fee: undefined,
      stations: [],
      specials: [],
      gratuity_fee: undefined,
      deposit: undefined,
      brokerage_fee: undefined,
      insurance_company: undefined,
      breadth_floor: undefined,
      madori: undefined,
      address: undefined,
      walks: [],
      build_const: undefined,
      building_age: undefined,
      other_initial_cost: undefined,
      build_type: undefined,
      introduction_status: undefined,
    },
    property_images: [],
  })

  const { showSnackbar } = useNotification()

  const notifyFavoritedProperty = async (favoritedProperty?: FavoritedProperty) => {
    if (favoritedProperty) {
      showSnackbar(
        `物件ID:${favoritedProperty.property_id}\nを\nユーザーID:${formState.userId}の持ち込み物件として登録しました〜`
      )
    }
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (e.target instanceof HTMLInputElement) {
      const value = e.target.value

      if (e.target.name.startsWith("favorited_property.")) {
        const name = e.target.name.split(".")[1]
        setFormState((currentState) => ({
          ...currentState,
          favorited_property: {
            ...currentState.favorited_property,
            [name]: e.target.value,
          },
        }))
      }

      setFormState((currentState) => ({
        ...currentState,
        property: {
          ...currentState.property,
          [e.target.name]: value,
        },
      }))
    }
  }

  const handleMultiChange = (
    name: "stations" | "specials" | "walks",
    value: string,
    action?: "add" | "remove"
  ) => {
    setFormState((currentState) => {
      const currentValue = currentState.property[name] as string[]
      const values = value.includes(",") ? value.split(",") : [value]

      let newProperty = [...currentValue]

      values.forEach((value) => {
        newProperty =
          action === "remove" ? newProperty.filter((d) => d !== value) : [...newProperty, value]
      })

      return {
        ...currentState,
        property: {
          ...currentState.property,
          [name]: newProperty,
        },
      }
    })
  }

  const handleUrlChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (e.target instanceof HTMLInputElement) {
      setFormState((currentState) => {
        return {
          ...currentState,
          [e.target.name]: e.target.value,
        }
      })
    }
  }

  const handleUrlSubmit = async (userId: string) => {
    showSnackbar(`スクレイピング中〜〜🙃`)

    const res = await axios
      .post(`${SMOOTH_LINE_API_URL}/base_api/smoother/scraping/run`, {
        uri: formState.url,
        user_id: userId,
      })
      .catch((e) => {
        showSnackbar(`スクレイピングできませんでした…`)

        throw new Error(e)
      })

    const data = res.data as {
      property: Property
      property_images: PropertyImage[]
      favorited_property: FavoritedProperty
    }

    setFormState((currentState) => ({
      ...currentState,
      url: "",
      property: data.property,
      property_images: data.property_images,
    }))

    showSnackbar(`${data.property.building_name}のスクレイピングに成功しました〜`)

    showSnackbar(`${data.property.building_name}（ID:${data.property.id}）を保存しました〜`)

    await notifyFavoritedProperty(data.favorited_property)
  }

  const handleImageSubmit = async (
    info: Pick<PropertyImage, "full_path" | "property_id">,
    action: "add" | "remove"
  ) => {
    if (action === "add") {
      const res = await axios.post(`${SMOOTH_LINE_API_URL}/base_api/smoother/property_images`, info)
      const data = res.data.property_image as PropertyImage

      setFormState((currentState) => ({
        ...currentState,
        property_images: [...currentState.property_images, data],
      }))

      showSnackbar(`画像ID:${data.id}を追加しました〜`)
    }
  }

  const handlePropertySubmit = async (userId: string) => {
    try {
      const type = formState.property?.id ? "update" : "add"

      const payload = {
        property: formState.property,
        user_id: userId,
      }

      const res =
        type === "update"
          ? await axios.put(
              `${SMOOTH_LINE_API_URL}/base_api/smoother/properties/${formState.property.id}`,
              payload
            )
          : await axios.post(`${SMOOTH_LINE_API_URL}/base_api/smoother/properties`, payload)

      const { property, favorited_property } = res.data as {
        property: Property
        favorited_property: FavoritedProperty
      }

      setFormState((currentState) => ({
        ...currentState,
        property,
      }))

      showSnackbar(
        type === "update"
          ? `物件ID:${property.id}を変更しました〜`
          : `物件ID:${property.id}を追加しました〜`
      )

      await notifyFavoritedProperty(favorited_property)
    } catch (e) {
      showSnackbar(`追加失敗しました！😇`)
    }
  }

  const handleRunNinja = async (uri: string, user_id: string) => {
    const payload = {
      uri,
      user_id,
    }

    try {
      await axios.post(
        `${SMOOTH_LINE_API_URL}/base_api/smoother/ninja/run_with_user_actions`,
        payload
      )
    } catch (e) {
      showSnackbar(`忍者失敗…かたじけない…`)
      return
    }

    showSnackbar("忍者発動！🥷")
  }

  const handleUserIdChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFormState((currentState) => ({
      ...currentState,
      userId: e.target.value,
    }))
  }

  return {
    formState,
    handleChange,
    handleUrlChange,
    handleUrlSubmit,
    handleMultiChange,
    handleImageSubmit,
    handlePropertySubmit,
    handleRunNinja,
    handleUserIdChange,
  }
}
